import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointment } from '../../context/appointmentContext';
import { usePostMessage } from '../../context/messageContext';
import { useTestContext } from '../../context/testContext';
import { useVideo } from '../../context/videoContext';
import { useAuth } from '../../context/authContext';
import { useSessionState } from '../../context/sessionStateContext';
import { client } from '../../helpers/api-client';
import { todayString } from '../../helpers/functions';
import { useResultData } from '../../helpers/useResultData';
import { useSessionUI } from '../../ui';

// Names/roles for the video tiles and headers.
export const useSessionPeople = () => {
  const { appointment, userRole } = useAppointment() || {};
  const appt = appointment?.currentAppointment || {};
  const me = appointment?.currentUser || {};
  const isPatient = (me.role || userRole) === 'patient';
  const clinicianName = appt.optician_full_name || (!isPatient ? me.full_name : '') || 'Clinician';
  const patientName = appt.patient_full_name || (isPatient ? me.full_name : '') || 'Patient';
  return {
    isPatient,
    appointmentId: appt.id,
    currentUserId: me.id,
    clinicianName,
    patientName,
    localLabel: isPatient ? 'Patient' : 'Clinician',
    localName: isPatient ? patientName : clinicianName,
    remoteLabel: isPatient ? 'Clinician' : 'Patient',
    remoteName: isPatient ? clinicianName : patientName,
  };
};

// Clinician "End Session": same API/ws behaviour as helpers/useEndSession, with the styled confirm.
export const useConfirmEndSession = () => {
  const { appointment } = useAppointment() || {};
  const { disconnect } = useVideo() || {};
  const postMessage = usePostMessage();
  const navigate = useNavigate();
  const { confirm, toast } = useSessionUI();
  const { recordEvent } = useSessionState();
  const { id: appointmentId, room_name } = appointment?.currentAppointment || {};

  return useCallback(async () => {
    const ok = await confirm({
      message: 'Are you sure you want to end the session?',
      detail: 'This session will no longer be available to you or the patient.',
      yesLabel: 'End Session',
      noLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await client(`api/appointments/${appointmentId}/`, { body: { status: 'completed', end: todayString() } }, 'PATCH');
    } catch (error) {
      toast({ tone: 'error', title: 'Could not end the session', body: error.message });
      return;
    }
    recordEvent('ended');
    disconnect?.();
    postMessage({ type: 'opticianLoggedOut', content: true });
    postMessage({ type: 'endedSession', room_name });
    navigate(`${process.env.PUBLIC_URL}/error`, {
      state: { error: 'This session has been closed. Close your browser to end the appointment.', title: 'Session closed' },
    });
  }, [appointmentId, room_name, confirm, toast, recordEvent, disconnect, postMessage, navigate]);
};

// Patient "Exit Session": leave the call (the appointment stays open for the clinician).
export const useConfirmExitSession = () => {
  const { logout } = useAuth() || {};
  const { disconnect } = useVideo() || {};
  const { confirm } = useSessionUI();
  const { recordEvent } = useSessionState();
  return useCallback(async () => {
    const ok = await confirm({ message: 'Are you sure you want to leave the session?', yesLabel: 'Leave', noLabel: 'Stay', tone: 'danger' });
    if (!ok) return;
    recordEvent('left');
    disconnect?.();
    logout?.();
  }, [confirm, recordEvent, disconnect, logout]);
};

// Clinician menu / step navigation. Mirrors components/Navigation.js (legacy ws messages,
// "you may lose test data" confirmation while a test is running).
export const CLINICIAN_STEPS = [
  { id: 'calibration-selection', label: 'Calibration Selection' },
  { id: 'set-distance', label: 'Set Distance' },
  { id: 'test', label: 'Examinations' },
  { id: 'results', label: 'Completed Examinations' },
];

export const useClinicianNavigation = (activeTab, setActiveTab) => {
  const postMessage = usePostMessage();
  const { clearAllResults } = useResultData();
  const { confirm } = useSessionUI();
  const { testData } = useTestContext() || {};
  const testInProgress = testData?.testInProgress;

  const go = useCallback(
    async (tab) => {
      if (testInProgress) {
        const ok = await confirm({
          message: 'Leave this test?',
          detail: 'Navigating away from this page may mean that you lose test data.',
          yesLabel: 'Leave',
          noLabel: 'Stay',
          tone: 'danger',
        });
        if (!ok) return;
        clearAllResults();
      }
      if (tab === 'consultationView') {
        postMessage({ type: 'consultationView' });
        postMessage({ type: 'endOfTest' });
      }
      if (tab === 'test') postMessage({ type: 'testNavigationReset' });
      if (tab === 'calibration-selection') postMessage({ type: 'testCalibrationReset' });
      setActiveTab(tab); // ClinicianSessionFrame mirrors the step into the session state
    },
    [testInProgress, confirm, clearAllResults, postMessage, setActiveTab],
  );

  return go;
};

export const buildMenuItems = ({ activeTab, onNavigate, onEnd, endLabel = 'End Session', readOnly = false }) => [
  ...CLINICIAN_STEPS.map((s) => ({
    id: s.id,
    label: s.label,
    current: activeTab === s.id,
    disabled: readOnly,
    onSelect: readOnly ? undefined : () => onNavigate(s.id),
  })),
  { id: 'end', label: endLabel, danger: true, onSelect: onEnd },
];
